//
// # UI State
//

// Workspace Imports
import { Point } from "GenAlphaModel";
import type { Editor } from "./editor";
import { MousePos } from "DrawCore/src/mousepos";
import { CountersDisplay } from "./counters";
import { WorldTransforms } from "DrawCore/src/transforms";

// # UI State
//
// Everything (OK, many things) about the current state of the UI that *is not* the content being edited.
//
export class UiState {
  // The perf-counters overlay
  counters: CountersDisplay;

  // Our sole contructor argument is the parent `Editor`.
  constructor(readonly editor: Editor) {
    this.counters = new CountersDisplay(editor);
  }

  // Position of the cursor/ mouse
  mousePos: MousePos = new MousePos(Point.origin(), this);

  // Latest keyboard modifier flags observed on a mouse event. Used by move-mode
  // handlers to read e.g. shift state from inside handleMouseMove without
  // needing the MouseEvent threaded through the ModeHandler signature.
  modifiers: { shift: boolean } = { shift: false };

  // Update the mouse-position from a `MouseEvent`, or anything else with {clientX, clientY} properties.
  // Converts to canvas-relative coordinates by subtracting the editor container's bounding rect.
  // Returns a read-only view of the newly generated `MousePos`.
  updateMousePos = (e: {
    clientX: number;
    clientY: number;
    shiftKey?: boolean;
  }): Readonly<MousePos> => {
    const rect = this.editor.parentDomElement.getBoundingClientRect();
    const page = Point.new(e.clientX - rect.left, e.clientY - rect.top);
    this.mousePos = new MousePos(page, this);
    this.modifiers.shift = e.shiftKey ?? false;

    this.counters.updateMousePos(this.mousePos.sch.x, this.mousePos.sch.y);

    return this.mousePos;
  };

  // Refresh the cached mouse position after the world transforms have changed
  // (e.g. after a pan). Creates a new MousePos from the same page coordinates,
  // clearing the cached schematic coordinates so they are recomputed on next access.
  refreshMousePos = (): void => {
    this.mousePos = new MousePos(this.mousePos.page, this);
  };

  get worldTransforms(): WorldTransforms {
    return this.editor.canvas.worldTransforms;
  }
}
